// src/lib/keepRecurringWindow.ts
//
// Session 35 / Chunk 5A. Lazy window-keeper for the materialized recurring-
// jobs feature. Called on calendar navigation: extends each active series
// forward through (visibleEnd + 30-day buffer) so the operator never
// navigates into a void of "should-be-here" occurrences.
//
// Design notes:
//   - PURE GENERATOR REUSE. Recurrence math is buildOccurrenceJobs from
//     src/lib/recurringJobs.ts. This file does zero date-stepping — only
//     the gap-detection + window-edge bookkeeping around the existing
//     generator. Single source of truth.
//   - Gap detection: two-step query (active series, then their max
//     materialized date). The supabase-js query builder doesn't express
//     correlated subqueries or GROUP BY/HAVING cleanly, and the recon
//     ruled out new DB objects tonight. Aggregation happens in TS over a
//     bounded result set (active series × their occurrences).
//   - Idempotency: plain INSERT. The partial unique index
//     idx_field_jobs_series_date_unique fails-loud on the rare cross-tab
//     race (one tab gets a unique-violation; same trade as Chunk 3).
//   - windowStart per series: max(materializedTo + 1 day, today). The
//     today-clamp prevents backfilling old dates if a series was ever
//     created and emptied — we only extend forward.
//   - Job number minting: one COUNT at the top of the run, then a single
//     monotonically-increasing index across every occurrence in this run,
//     irrespective of which series owns it. Matches Chunk 3's pattern.
// ============================================================================

import { addDays, format, parseISO } from 'date-fns'
import type { SupabaseClient } from '@supabase/supabase-js'
import { buildOccurrenceJobs } from '@/lib/recurringJobs'
import type { Database } from '@/lib/database.types'

type SeriesRow = Database['public']['Tables']['recurring_job_series']['Row']

export interface KeepResult {
  /** Number of series that had a gap and were extended. */
  extended: number
  /** Total occurrences inserted across all extended series. */
  occurrences: number
}

const WINDOW_BUFFER_DAYS = 30

export async function keepRecurringWindow(
  supabase: SupabaseClient<Database>,
  workspaceId: string,
  visibleEnd: string,
): Promise<KeepResult> {
  if (!workspaceId || !visibleEnd) return { extended: 0, occurrences: 0 }

  // Target horizon: 30 days past the visible end so month-by-month nav
  // stays in the green zone without firing on every click.
  const targetHorizon = format(addDays(parseISO(visibleEnd), WINDOW_BUFFER_DAYS), 'yyyy-MM-dd')
  const today = format(new Date(), 'yyyy-MM-dd')

  // Step 1 — active series in this workspace. status='active' is the only
  // filter; the generator handles end_date / end_after_count internally.
  const { data: series, error: sErr } = await supabase
    .from('recurring_job_series')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('status', 'active')
  if (sErr) {
    console.error('[keep-recurring] active-series query failed:', sErr)
    throw sErr
  }
  if (!series || series.length === 0) return { extended: 0, occurrences: 0 }

  // Step 2 — per-series max(scheduled_date). One IN query, max computed in TS.
  const seriesIds = series.map(s => s.id)
  const { data: dateRows, error: dErr } = await supabase
    .from('field_jobs')
    .select('series_id, scheduled_date')
    .in('series_id', seriesIds)
  if (dErr) {
    console.error('[keep-recurring] occurrence-dates query failed:', dErr)
    throw dErr
  }

  const maxBySeries = new Map<string, string>()
  for (const row of dateRows ?? []) {
    if (!row.series_id || !row.scheduled_date) continue
    const cur = maxBySeries.get(row.series_id)
    if (!cur || row.scheduled_date > cur) maxBySeries.set(row.series_id, row.scheduled_date)
  }

  // Gap-bearing = no occurrences yet, OR latest occurrence is before our target.
  const gapBearing = series.filter(s => {
    const materializedTo = maxBySeries.get(s.id)
    return !materializedTo || materializedTo < targetHorizon
  })
  if (gapBearing.length === 0) return { extended: 0, occurrences: 0 }

  // Build the per-series occurrence batches via the SHARED generator. No
  // date-stepping logic in this file — that lives in recurringJobs.ts.
  const allInserts: ReturnType<typeof buildOccurrenceJobs> = []
  for (const s of gapBearing) {
    const materializedTo = maxBySeries.get(s.id)
    const windowStartCandidate = materializedTo
      ? format(addDays(parseISO(materializedTo), 1), 'yyyy-MM-dd')
      : today
    // Clamp to today so we never backfill historical dates if a series was
    // created and its prior occurrences were wiped.
    const windowStart = windowStartCandidate > today ? windowStartCandidate : today
    if (windowStart > targetHorizon) continue
    const inserts = buildOccurrenceJobs(s as SeriesRow, windowStart, targetHorizon)
    if (inserts.length > 0) allInserts.push(...inserts)
  }

  if (allInserts.length === 0) return { extended: 0, occurrences: 0 }

  // Job-number minting: one COUNT, one running index across all inserts in
  // this run. Cross-process races (another tab creating jobs concurrently)
  // could still collide; partial unique catches duplicate (series_id, date),
  // and job_number isn't currently unique-constrained.
  const { count: jobCount } = await supabase
    .from('field_jobs')
    .select('id', { count: 'exact', head: true })
    .eq('workspace_id', workspaceId)
  const baseCount = jobCount ?? 0

  const withNumbers = allInserts.map((j, i) => ({
    ...j,
    job_number: `JOB-${String(baseCount + i + 1).padStart(4, '0')}`,
  }))

  const { error: iErr } = await supabase.from('field_jobs').insert(withNumbers)
  if (iErr) {
    // 23505 = a concurrent writer (another tab, or the server
    // recurring_job_generate_sweep) materialized one of these
    // (series_id, scheduled_date) pairs between our gap computation and this
    // insert; the partial unique index idx_field_jobs_series_date_unique
    // aborted the batch. Swallow ONLY the unique violation — the other writer
    // covered the window, so this is a no-op, not a failure — and report zero
    // new occurrences. Any OTHER error is real and still throws. (No
    // ON CONFLICT: PostgREST cannot target a partial index.)
    if ((iErr as { code?: string }).code === '23505') {
      console.warn('[keep-recurring] occurrence INSERT raced (23505) — another writer filled the window; no-op')
      return { extended: 0, occurrences: 0 }
    }
    console.error('[keep-recurring] occurrence INSERT failed:', iErr)
    throw iErr
  }

  return { extended: gapBearing.length, occurrences: withNumbers.length }
}
